import { Fragment, ReactNode } from 'react';
import styles from './Markdown.module.css';

// Lightweight, dependency-free markdown renderer for support ticket content.
// It builds React elements directly (never dangerouslySetInnerHTML), so any
// text is escaped by React and the output is safe to show customer content.

interface InlineMatch {
  index: number;
  length: number;
  node: (key: string) => ReactNode;
}

// Customer-synced markdown can contain hostile URLs (e.g. `javascript:`), so only
// allow web/mail schemes (and scheme-less relative URLs); anything else is dropped.
const hasScheme = (url: string) => /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url.trim());

const safeHref = (url: string): string | undefined => {
  const trimmed = url.trim();
  if (!hasScheme(trimmed)) return trimmed;
  return /^(https?|mailto):/i.test(trimmed) ? trimmed : undefined;
};

const safeImageSrc = (url: string): string | undefined => {
  const trimmed = url.trim();
  if (!hasScheme(trimmed)) return trimmed;
  return /^https?:/i.test(trimmed) ? trimmed : undefined;
};

const inlineRules: { pattern: RegExp; build: (match: RegExpExecArray, key: string) => ReactNode }[] = [
  {
    pattern: /!\[([^\]]*)\]\(([^)\s]+)\)/,
    build: ([, alt, src], key) => {
      const source = safeImageSrc(src);
      return source ? <img key={key} src={source} alt={alt} /> : <Fragment key={key}>{alt}</Fragment>;
    },
  },
  {
    pattern: /\[([^\]]+)\]\(([^)\s]+)\)/,
    build: ([, text, href], key) => {
      const target = safeHref(href);
      return target ? (
        <a key={key} href={target} target="_blank" rel="noreferrer">
          {parseInline(text, key)}
        </a>
      ) : (
        <Fragment key={key}>{parseInline(text, key)}</Fragment>
      );
    },
  },
  {
    pattern: /`([^`]+)`/,
    build: ([, code], key) => <code key={key}>{code}</code>,
  },
  {
    pattern: /\*\*([^*\s](?:[^*]*[^*\s])?)\*\*|(?<![A-Za-z0-9])__([^_]+)__(?![A-Za-z0-9])/,
    build: (match, key) => <strong key={key}>{parseInline(match[1] ?? match[2], key)}</strong>,
  },
  {
    pattern: /\*([^*\s](?:[^*]*[^*\s])?)\*|(?<![A-Za-z0-9])_([^_]+)_(?![A-Za-z0-9])/,
    build: (match, key) => <em key={key}>{parseInline(match[1] ?? match[2], key)}</em>,
  },
  {
    pattern: /~~([^~]+)~~/,
    build: ([, text], key) => <del key={key}>{parseInline(text, key)}</del>,
  },
];

const parseInline = (text: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let remaining = text;
  let cursor = 0;

  while (remaining.length > 0) {
    let earliest: InlineMatch | null = null;

    inlineRules.forEach((rule, ruleIndex) => {
      const match = rule.pattern.exec(remaining);
      if (match && (earliest === null || match.index < earliest.index)) {
        earliest = {
          index: match.index,
          length: match[0].length,
          node: (key) => rule.build(match, `${key}-r${ruleIndex}`),
        };
      }
    });

    if (earliest === null) {
      nodes.push(remaining);
      break;
    }

    const found: InlineMatch = earliest;
    if (found.index > 0) {
      nodes.push(remaining.slice(0, found.index));
    }
    nodes.push(found.node(`${keyPrefix}-i${cursor}`));
    remaining = remaining.slice(found.index + found.length);
    cursor += 1;
  }

  return nodes;
};

const isOrderedItem = (line: string) => /^\s*\d+\.\s+/.test(line);
const isUnorderedItem = (line: string) => /^\s*[-*+]\s+/.test(line);

const renderListItems = (lines: string[], keyPrefix: string): ReactNode[] =>
  lines.map((line, index) => (
    <li key={`${keyPrefix}-li${index}`}>
      {parseInline(line.replace(/^\s*(?:\d+\.|[-*+])\s+/, ''), `${keyPrefix}-li${index}`)}
    </li>
  ));

const renderBlocks = (markdown: string): ReactNode[] => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let index = 0;
  let blockKey = 0;

  while (index < lines.length) {
    const line = lines[index];
    const key = `b${blockKey++}`;

    if (line.trim() === '') {
      index += 1;
      continue;
    }

    // Fenced code block
    const fence = line.match(/^```(.*)$/);
    if (fence) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index])) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1; // skip closing fence
      blocks.push(
        <pre key={key}>
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4';
      blocks.push(<Tag key={key}>{parseInline(heading[2], key)}</Tag>);
      index += 1;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      blocks.push(<hr key={key} />);
      index += 1;
      continue;
    }

    // Blockquote
    if (/^\s*>/.test(line)) {
      const quoteLines: string[] = [];
      while (index < lines.length && /^\s*>/.test(lines[index])) {
        quoteLines.push(lines[index].replace(/^\s*>\s?/, ''));
        index += 1;
      }
      blocks.push(<blockquote key={key}>{renderBlocks(quoteLines.join('\n'))}</blockquote>);
      continue;
    }

    // Ordered list
    if (isOrderedItem(line)) {
      const itemLines: string[] = [];
      while (index < lines.length && isOrderedItem(lines[index])) {
        itemLines.push(lines[index]);
        index += 1;
      }
      blocks.push(<ol key={key}>{renderListItems(itemLines, key)}</ol>);
      continue;
    }

    // Unordered list
    if (isUnorderedItem(line)) {
      const itemLines: string[] = [];
      while (index < lines.length && isUnorderedItem(lines[index])) {
        itemLines.push(lines[index]);
        index += 1;
      }
      blocks.push(<ul key={key}>{renderListItems(itemLines, key)}</ul>);
      continue;
    }

    // Paragraph: gather consecutive non-blank, non-structural lines
    const paragraphLines: string[] = [];
    while (
      index < lines.length &&
      lines[index].trim() !== '' &&
      !/^```/.test(lines[index]) &&
      !/^(#{1,4})\s+/.test(lines[index]) &&
      !/^\s*>/.test(lines[index]) &&
      !isOrderedItem(lines[index]) &&
      !isUnorderedItem(lines[index])
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }
    blocks.push(
      <p key={key}>
        {paragraphLines.map((paragraphLine, lineIndex) => (
          <Fragment key={`${key}-l${lineIndex}`}>
            {lineIndex > 0 && <br />}
            {parseInline(paragraphLine, `${key}-l${lineIndex}`)}
          </Fragment>
        ))}
      </p>
    );
  }

  return blocks;
};

const Markdown = (props: { content: string }): JSX.Element => {
  const { content } = props;
  return <div className={styles.md}>{renderBlocks(content)}</div>;
};

export default Markdown;
